import { MarketOutcome } from '../../enums';
import { MarketConfig } from '../../interfaces';
import {
	extractNumber,
	extractSelection,
	resolveCondition,
	getPlayerResult,
	getPlayerStats,
} from '../../utils';
import { resolveGolfMoneyline } from '../../utils/market/golf.utils';

const ORDINALS = [
	'1st',
	'2nd',
	'3rd',
	'4th',
	'5th',
	'6th',
	'7th',
	'8th',
	'9th',
	'10th',
	'11th',
	'12th',
	'13th',
	'14th',
	'15th',
	'16th',
	'17th',
	'18th',
];

function buildHoleConfig(ordinal: string, holeNumber: number): MarketConfig {
	return {
		buildQuestion: (m) => `Will ${m.selection} score under par on the ${ordinal} hole?`,
		resolve: (q, gc, r) => {
			const playerName = extractSelection(q, 'Will ', ['score under par']);
			const stats = getPlayerStats(playerName, r);
			const value = stats?.[`strokes_hole_${holeNumber}`] ?? null;
			if (value === null) return [MarketOutcome.Pending];

			return [resolveCondition(value < 0, gc.isCompleted)];
		},
	};
}

const holeConfigs: Record<string, MarketConfig> = {};
ORDINALS.forEach((ordinal, i) => {
	holeConfigs[`${ordinal} Hole Player Relative Score`] = buildHoleConfig(ordinal, i + 1);
});

export const golfConfig: Record<string, MarketConfig> = {
	// ── H2H Matchup Markets (Fanatics + FanDuel) ──────────────────────

	Moneyline: {
		buildQuestion: (m) => `Will ${m.selection} win?`,
		isFinalRequired: true,
		resolve: (q, gc) => resolveGolfMoneyline(q, gc),
	},

	'1st Round Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 1st round matchup?`,
		resolve: (q, gc) => resolveGolfMoneyline(q, gc),
	},

	'2nd Round Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 2nd round matchup?`,
		resolve: (q, gc) => resolveGolfMoneyline(q, gc),
	},

	'3rd Round Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 3rd round matchup?`,
		resolve: (q, gc) => resolveGolfMoneyline(q, gc),
	},

	'4th Round Moneyline': {
		buildQuestion: (m) => `Will ${m.selection} win the 4th round matchup?`,
		resolve: (q, gc) => resolveGolfMoneyline(q, gc),
	},

	// ── Player Prop Markets (FanDuel, tournament-level) ────────────────

	'Player Strokes': {
		buildQuestion: (m) => `Will ${m.selection} finish with fewer than ${m.points} strokes?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const playerName = extractSelection(q, 'Will ', ['finish with fewer']);
			const playerResult = getPlayerResult(playerName, r);
			const value = playerResult?.player_strokes ?? 0;

			return [resolveCondition(value > 0 && value < threshold, gc.isCompleted), value];
		},
	},

	'Player Birdies Or Better': {
		buildQuestion: (m) => `Will ${m.selection} make at least ${m.points} birdies or better?`,
		hasProgressBar: true,
		resolve: (q, gc, r) => {
			const threshold = extractNumber(q);
			const playerName = extractSelection(q, 'Will ', ['make at least']);
			const playerResult = getPlayerResult(playerName, r);
			const value = playerResult?.player_birdies_or_better ?? 0;

			return [resolveCondition(value >= threshold, gc.isCompleted), value];
		},
	},

	// ── Per-Hole Player Relative Score (FanDuel, tournament-level) ─────
	...holeConfigs,
};
